import { useState } from 'react'
import { usePayments, useSettings } from '@/lib/queries'
import type { PaymentFilters } from '@/lib/queries'
import { PageHeader, Card, PageStack } from '@/components/ui/Page'
import { Table } from '@/components/ui/Table'
import type { Column } from '@/components/ui/Table'
import { Pagination } from '@/components/ui/Pagination'
import { PaymentStatusPill } from '@/components/ui/Pill'
import { formatDateTime, formatMoney } from '@/lib/format'
import type { Payment } from '@/types/api'
import shared from '@/features/bookings/BookingsPage.module.css'

const GATEWAY_STATUSES = [
  { value: '', label: 'Any status' },
  { value: 'SUCCESS', label: 'Successful' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'FAILED', label: 'Failed' },
  { value: 'REFUNDED', label: 'Refunded' },
  { value: 'PARTIALLY_REFUNDED', label: 'Partially refunded' },
]

/*
  *** A CASH CLIENT CANNOT REACH MOST OF THOSE STATES ***
  There is no gateway to decline a card or process a refund, so offering
  "Partially refunded" as a filter on a cash tenant promises a state that
  cannot occur. What a counter actually produces is: collected, waiting, or
  turned down.
*/
const CASH_STATUSES = [
  { value: '', label: 'Any status' },
  { value: 'SUCCESS', label: 'Collected' },
  { value: 'PENDING', label: 'Awaiting collection' },
  { value: 'FAILED', label: 'Turned down' },
]

/**
 * A14 — Payments.
 *
 * This screen exists for reconciliation and disputes, so it is built around
 * the two questions those actually involve:
 *
 *   "Did this student's payment go through?"    → search by name
 *   "What is reference pay_XXXX in our system?" → search by provider id
 *
 * The provider's payment id is therefore a visible, copyable column rather than
 * a detail hidden behind a row click — it is the reference quoted to the
 * gateway, and retyping it from a modal is how transcription errors happen.
 */
export function PaymentsPage() {
  // *** WHO TOOK THE MONEY CHANGES WHAT THIS SCREEN MEANS ***
  // On a cash client there is no gateway, so a "Gateway reference" column is
  // a column of dashes and a refund filter offers a state that cannot occur.
  // The rows are still real — a cash approval writes a payment carrying the
  // collection note — so the page stays and reports what actually happened.
  const settings = useSettings()
  const isCash = settings.data?.payment_mode === 'CASH'

  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('')
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')

  const filters: PaymentFilters = {
    page,
    page_size: pageSize,
    search: search || undefined,
    status: status || undefined,
    date_from: dateFrom || undefined,
    date_to: dateTo || undefined,
  }

  const { data, isLoading, isFetching, error, refetch } = usePayments(filters)
  const hasFilters = Boolean(search || status || dateFrom || dateTo)

  const columns: Array<Column<Payment>> = [
    {
      key: 'when',
      header: 'Date',
      render: (row) => (
        <div>
          <span className={shared.primary}>
            {formatDateTime(row.paid_at ?? row.created_at)}
          </span>
          <p className={shared.secondary}>
            {row.provider === 'cash' ? 'At the counter' : row.provider}
          </p>
        </div>
      ),
    },
    {
      key: 'student',
      header: 'Student',
      render: (row) => (
        <div>
          <span className={shared.primary}>{row.student_name}</span>
          <p className={shared.secondary}>with {row.mentor_name}</p>
        </div>
      ),
    },
    {
      key: 'reference',
      // The cash equivalent of a gateway reference: the note an admin wrote
      // when they took the money, which is the only record it happened.
      header: isCash ? 'Receipt note' : 'Gateway reference',
      secondary: true,
      render: (row) => {
        if (isCash) {
          return row.collection_note ? (
            <span className={shared.secondary}>{row.collection_note}</span>
          ) : (
            // Not an error: the note is optional, so an admin clearing a queue
            // quickly may have left it blank.
            <span className={shared.muted}>No note</span>
          )
        }
        return row.provider_payment_id ? (
          // Selectable in one click: this gets pasted into a gateway dashboard.
          <span className={shared.mono} style={{ userSelect: 'all' }}>
            {row.provider_payment_id}
          </span>
        ) : (
          <span className={shared.muted}>—</span>
        )
      },
    },
    {
      key: 'status',
      header: 'Status',
      render: (row) => (
        <div className={shared.statusCell}>
          <PaymentStatusPill status={row.status} />
          {row.failure_reason && (
            // The gateway's own words. An admin explaining a decline to a
            // student needs the reason, not just "failed".
            <span className={shared.secondary}>{row.failure_reason}</span>
          )}
        </div>
      ),
    },
    {
      key: 'amount',
      header: 'Amount',
      align: 'right',
      render: (row) => (
        <div>
          <span className={`${shared.primary} numeric`}>
            {formatMoney(row.amount, row.currency)}
          </span>
          {row.refunded_amount && Number(row.refunded_amount) > 0 && (
            <p className={`${shared.refund} numeric`}>
              −{formatMoney(row.refunded_amount, row.currency)} refunded
            </p>
          )}
        </div>
      ),
    },
  ]

  return (
    <PageStack>
      <PageHeader
        title="Payments"
        description={
          isCash
            ? 'Money collected at the counter, and who confirmed it.'
            : 'Money received, for reconciliation against your payment gateway.'
        }
      />

      <Card flush>
        <div className={shared.filters}>
          <input
            type="search"
            value={search}
            onChange={(event) => {
              setSearch(event.target.value)
              setPage(1)
            }}
            placeholder={
              isCash
                ? 'Search student…'
                : 'Search student or gateway reference…'
            }
            className={shared.search}
            aria-label="Search payments"
          />

          <select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value)
              setPage(1)
            }}
            className={shared.select}
            aria-label="Filter by status"
          >
            {(isCash ? CASH_STATUSES : GATEWAY_STATUSES).map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>

          <div className={shared.dateRange}>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => {
                setDateFrom(event.target.value)
                setPage(1)
              }}
              className={shared.select}
              aria-label="Payments from"
            />
            <span className={shared.dateSeparator}>to</span>
            <input
              type="date"
              value={dateTo}
              onChange={(event) => {
                setDateTo(event.target.value)
                setPage(1)
              }}
              className={shared.select}
              aria-label="Payments until"
            />
          </div>

          {hasFilters && (
            <button
              type="button"
              className={shared.clear}
              onClick={() => {
                setSearch('')
                setStatus('')
                setDateFrom('')
                setDateTo('')
                setPage(1)
              }}
            >
              Clear
            </button>
          )}
        </div>

        <Table
          columns={columns}
          rows={data?.payments ?? []}
          rowKey={(row) => row.payment_id}
          loading={isLoading || isFetching}
          error={error}
          onRetry={() => void refetch()}
          isFiltered={hasFilters}
          emptyMessage="No payments recorded yet."
          emptyFilteredMessage="No payments match these filters."
        />
      </Card>

      {data?.page && (
        <Pagination
          page={data.page}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
        />
      )}
    </PageStack>
  )
}
